import { h, Fragment } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import register from "preact-custom-element";
import Fuse from "fuse.js";
import storefront from "../helpers/storefront";
import unwrapNodes from "../helpers/unwrapNodes";
import { CloseIcon, SearchIcon } from "../helpers/icons";
import ReplacementPartsResult from "./replacement-parts-result";

const LISTBOX_ID = "replacement-parts-listbox";

const FUSE_OPTIONS = {
  keys: ["title", "sku", "barcode"],
  threshold: 0.4,
  minMatchCharLength: 2,
};

const GET_REPLACEMENT_PARTS = `
  query getReplacementParts($after: String) {
    products(first: 250, after: $after, query: "tag:replacement-part") {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        title
        handle
        featuredImage {
          url
          altText
        }
        variants(first: 10) {
          nodes {
            id
            title
            sku
            barcode
            price {
              amount
              currencyCode
            }
            availableForSale
            image {
              url
              altText
            }
          }
        }
        sparePartFor: metafield(namespace: "custom", key: "spare_part_for_products") {
          references(first: 50) {
            nodes {
              ... on Product {
                id
                title
                handle
                featuredImage {
                  url
                  altText
                }
                manualLinks: metafield(namespace: "specifications", key: "manual_link") {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

async function fetchAllReplacementParts() {
  const allParts = [];
  let after = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const { data } = await storefront.request({
      query: GET_REPLACEMENT_PARTS,
      variables: { after },
    });

    const page = data?.products;
    if (!page) break;

    allParts.push(...page.nodes.map(unwrapNodes));
    hasNextPage = page.pageInfo.hasNextPage;
    after = page.pageInfo.endCursor;
  }
  return allParts;
}

function buildSearchIndex(allParts) {
  const productMap = new Map();

  for (const part of allParts) {
    const products = Array.isArray(part.sparePartFor) ? part.sparePartFor : [];
    for (const product of products) {
      if (!productMap.has(product.id)) {
        productMap.set(product.id, product);
      }
    }
  }

  const searchItems = [];

  for (const [productId, product] of productMap) {
    const parts = allParts.filter(
      (p) =>
        Array.isArray(p.sparePartFor) &&
        p.sparePartFor.some((ref) => ref.id === productId),
    );
    searchItems.push({
      type: "product",
      id: productId,
      title: product.title,
      product,
      parts,
    });
  }

  const partsByTitle = new Map();
  for (const part of allParts) {
    const variants = Array.isArray(part.variants) ? part.variants : [];
    const sku = variants
      .filter((v) => v.sku)
      .map((v) => v.sku)
      .join(" ");
    const barcode = variants
      .filter((v) => v.barcode)
      .map((v) => v.barcode)
      .join(" ");
    if (partsByTitle.has(part.title)) {
      const existing = partsByTitle.get(part.title);
      existing.parts.push(part);
      if (sku) existing.sku = (existing.sku + " " + sku).trim();
      if (barcode) existing.barcode = (existing.barcode + " " + barcode).trim();
    } else {
      partsByTitle.set(part.title, {
        type: "part",
        id: part.id,
        title: part.title,
        sku,
        barcode,
        parts: [part],
      });
    }
  }
  searchItems.push(...partsByTitle.values());

  return searchItems;
}

function dedupParts(parts) {
  const seen = new Set();
  return parts.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}

function updateUrl(q) {
  const url = new URL(window.location);
  if (q) {
    url.searchParams.set("q", q);
  } else {
    url.searchParams.delete("q");
  }
  history.replaceState(null, "", url);
}

function ReplacementParts({
  eyebrow,
  heading,
  textColor,
  bgDesktop,
  bgMobile,
  fpDesktop,
  fpMobile,
}) {
  const [searchItems, setSearchItems] = useState(null);
  const [query, setQuery] = useState(
    () => new URLSearchParams(window.location.search).get("q") || "",
  );
  const [selectedItem, setSelectedItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedOptionId, setFocusedOptionId] = useState(null);
  const [showNoResults, setShowNoResults] = useState(false);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const blurTimerRef = useRef(null);
  const filteredRef = useRef([]);
  const queryRef = useRef(query);
  const fuseRef = useRef(null);
  const searchItemsRef = useRef(null);

  useEffect(() => {
    fetchAllReplacementParts().then((parts) => {
      setSearchItems(buildSearchIndex(parts));
    });
  }, []);

  // On load, run a search for ?q= and show results as if the user submitted
  useEffect(() => {
    if (!searchItems) return;
    const q = new URLSearchParams(window.location.search).get("q");
    if (!q) return;
    const exactProduct = searchItems.find(
      (i) => i.type === "product" && i.title.toLowerCase() === q.toLowerCase(),
    );

    let item;
    if (exactProduct) {
      item = exactProduct;
    } else {
      const results = new Fuse(searchItems, FUSE_OPTIONS)
        .search(q)
        .map((r) => r.item);
      if (results.length === 0) return;
      const firstProduct = results.find((r) => r.type === "product");
      if (firstProduct) {
        item = firstProduct;
      } else {
        const first = results[0];
        item = {
          type: "part",
          id: first.id,
          title: q,
          parts: dedupParts(results.flatMap((i) => i.parts)),
        };
      }
    }

    setSelectedItem(item);
    setQuery("");
    setFocusedOptionId(null);
  }, [searchItems]);

  useEffect(() => {
    const el = searchRef.current;
    if (!el) return;

    const handleChange = (e) => {
      const item = (searchItemsRef.current || []).find(
        (i) => i.id === e.detail.value,
      );
      if (!item) return;
      if (item.type === "part") {
        const results = fuseRef.current
          ? fuseRef.current.search(item.title).map((r) => r.item)
          : [item];
        const allParts = dedupParts(results.flatMap((i) => i.parts));
        submit({
          type: "part",
          id: item.id,
          title: item.title,
          parts: allParts,
        });
      } else {
        submit(item);
      }
    };

    const handleFocusChange = () => {
      const focused = el.querySelector("[data-focused]");
      setFocusedOptionId(focused?.id ?? null);
    };

    el.addEventListener("aria-listbox:change", handleChange);
    el.addEventListener("aria-listbox:focus-change", handleFocusChange);

    return () => {
      el.removeEventListener("aria-listbox:change", handleChange);
      el.removeEventListener("aria-listbox:focus-change", handleFocusChange);
    };
  }, []);

  const fuse = useMemo(
    () => (searchItems ? new Fuse(searchItems, FUSE_OPTIONS) : null),
    [searchItems],
  );

  const filtered =
    fuse && query.length > 0 ? fuse.search(query).map((r) => r.item) : [];
  filteredRef.current = filtered;
  queryRef.current = query;
  fuseRef.current = fuse;
  searchItemsRef.current = searchItems;

  const showDropdown = isOpen && filtered.length > 0;

  const submit = (item) => {
    setSelectedItem(item);
    setQuery("");
    setIsOpen(false);
    setFocusedOptionId(null);
    setShowNoResults(false);
    updateUrl(item.title);
  };

  const handleInput = (e) => {
    clearTimeout(blurTimerRef.current);
    const val = e.target.value;
    setQuery(val);
    setSelectedItem(null);
    setIsOpen(val.length > 0);
    setFocusedOptionId(null);
    setShowNoResults(false);
    updateUrl(val);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setFocusedOptionId(null);
    } else if (e.key === "Enter") {
      if (isOpen && filtered.length > 0) {
        e.preventDefault();
        const hasFocused = searchRef.current?.querySelector("[data-focused]");
        if (!hasFocused) {
          const allParts = dedupParts(filtered.flatMap((i) => i.parts));
          submit({
            type: "part",
            id: filtered[0].id,
            title: query,
            parts: allParts,
          });
        }
      } else if (query.length > 0 && searchItems !== null) {
        e.preventDefault();
        setShowNoResults(true);
        setIsOpen(false);
      }
    }
  };

  const handleClear = () => {
    setQuery("");
    setSelectedItem(null);
    setIsOpen(false);
    setFocusedOptionId(null);
    setShowNoResults(false);
    inputRef.current?.focus();
    updateUrl("");
  };

  const handleFocus = () => {
    clearTimeout(blurTimerRef.current);
    if (query.length > 0) setIsOpen(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  const headerStyle = textColor
    ? `--replacement-parts-text-color: ${textColor}`
    : undefined;
  return (
    <>
      <div class="replacement-parts__header" style={headerStyle}>
        {(bgDesktop || bgMobile) && (
          <div class="replacement-parts__background" aria-hidden="true">
            <picture>
              {bgMobile && (
                <source media="(max-width: 767px)" srcSet={bgMobile} />
              )}
              <img
                src={bgDesktop || bgMobile}
                alt=""
                loading="eager"
                style={{
                  ...(fpDesktop ? { "--fp-desktop": fpDesktop } : {}),
                  ...(fpMobile ? { "--fp-mobile": fpMobile } : {}),
                }}
              />
            </picture>
          </div>
        )}
        {eyebrow && <span class="replacement-parts__eyebrow">{eyebrow}</span>}
        {heading && <h2 class="replacement-parts__title">{heading}</h2>}
        <div ref={searchRef} class="replacement-parts__search">
          <div
            class={`replacement-parts__input-wrapper${showDropdown ? " replacement-parts__input-wrapper--open" : ""}`}
          >
            <span class="replacement-parts__search-icon">
              <SearchIcon />
            </span>
            <label class="visually-hidden" for="replacement-parts-input">
              Search for product name or part
            </label>
            <input
              ref={inputRef}
              id="replacement-parts-input"
              type="text"
              class="replacement-parts__input"
              placeholder="Search by product name, part name, or SKU"
              value={query}
              autocomplete="off"
              role="combobox"
              aria-expanded={isOpen && filtered.length > 0}
              aria-haspopup="listbox"
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={focusedOptionId}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            {query.length > 0 && (
              <button
                type="button"
                class="replacement-parts__clear"
                aria-label="Clear search"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClear}
              >
                <CloseIcon />
              </button>
            )}
          </div>

          {showDropdown && (
            <aria-listbox-v2
              id={LISTBOX_ID}
              class="replacement-parts__dropdown"
              ACTIVE
              onMouseDown={(e) => e.preventDefault()}
            >
              {filtered.slice(0, 6).map((item, i) => (
                <div
                  key={item.id}
                  id={`replacement-parts-option-${i}`}
                  class="replacement-parts__option"
                  role="option"
                  data-value={item.id}
                >
                  {item.type === "product"
                    ? `Parts for ${item.title}`
                    : item.title}
                </div>
              ))}
            </aria-listbox-v2>
          )}
          {showNoResults && (
            <p class="replacement-parts__no-results" role="alert">
              No results found for &ldquo;{query}&rdquo;.
            </p>
          )}
        </div>
      </div>

      <div class="replacement-parts__results">
        {selectedItem && (
          <ReplacementPartsResult
            type={selectedItem.type}
            product={selectedItem.product}
            parts={selectedItem.parts}
          />
        )}
      </div>
    </>
  );
}

if (!customElements.get("replacement-parts")) {
  register(
    ReplacementParts,
    "replacement-parts",
    [
      "eyebrow",
      "heading",
      "text-color",
      "bg-desktop",
      "bg-mobile",
      "fp-desktop",
      "fp-mobile",
    ],
    {
      shadow: false,
    },
  );
}
